import React, { useEffect, useState } from "react";
import { Box, Paper, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { FormattedMessage } from "react-intl";

const TableOfContents = ({ sections = [] }) => {
  const [activeId, setActiveId] = useState("");
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      let nextActiveId = "";

      sections.forEach((section) => {
        const element = document.getElementById(section.id);
        if (!element) {
          return;
        }

        const bounds = element.getBoundingClientRect();
        if (bounds.top <= 96) {
          nextActiveId = section.id;
        }
      });

      setActiveId(nextActiveId);

      const related = document.getElementById("related-articles");
      if (related) {
        setHidden(related.getBoundingClientRect().top <= window.innerHeight);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, [sections]);

  if (!sections.length) {
    return null;
  }

  return (
    <Box
      component={motion.nav}
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: hidden ? 0 : 1, x: hidden ? 12 : 0 }}
      transition={{ duration: 0.5, delay: 0.3 }}
      sx={{
        display: { xs: "none", xl: "block" },
        position: "fixed",
        right: 32,
        bottom: 32,
        zIndex: 1200,
        maxWidth: 240,
        pointerEvents: hidden ? "none" : "auto",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          backgroundColor: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(10px)",
          boxShadow: "0 1px 3px hsl(150 30% 14% / 0.06), 0 8px 24px -8px hsl(150 30% 14% / 0.1)",
        }}
      >
        <Typography
          sx={{
            mb: 1.5,
            fontSize: 10,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            color: "text.secondary",
            fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
          }}
        >
          <FormattedMessage id="blog.table_of_contents" />
        </Typography>

        <Box component="ul" sx={{ m: 0, p: 0, listStyle: "none", display: "grid", gap: 1 }}>
          {sections.map((section) => {
            const isActive = activeId === section.id;
            return (
              <Box key={section.id} component="li">
                <Box
                  component="a"
                  href={`#${section.id}`}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    textDecoration: "none",
                    "&:hover .toc-bar": { backgroundColor: "hsl(152 60% 32%)" },
                    "&:hover .toc-label": { color: "hsl(150 37% 17%)" },
                  }}
                >
                  <Box
                    className="toc-bar"
                    sx={{
                      flexShrink: 0,
                      width: 3,
                      alignSelf: "stretch",
                      borderRadius: "999px",
                      backgroundColor: isActive ? "hsl(152 60% 32%)" : "transparent",
                      transition: "background-color 0.2s ease",
                    }}
                  />
                  <Box
                    className="toc-label"
                    sx={{
                      fontSize: 12,
                      lineHeight: 1.4,
                      color: isActive ? "hsl(152 60% 32%)" : "text.secondary",
                      transition: "color 0.2s ease",
                      fontFamily: isActive ? "Segoe UI Semibold, system-ui, sans-serif" : "Segoe UI Regular, system-ui, sans-serif",
                    }}
                  >
                    {section.title}
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
};

export default TableOfContents;
